import { Image } from '@unistocks/components/basic/image';
import { Form } from '@unistocks/components/form/form';
import { FormFieldTransform } from '@unistocks/components/form/model';
import { oneOf, requiredSchema } from '@unistocks/components/form/validators';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { useAssetSearch } from '@unistocks/features/assets/hooks/use-asset-search';
import { PeriodSelect } from '@unistocks/features/dashboard/components/settings/period-select';
import { PortfolioFilter } from '@unistocks/features/dashboard/components/settings/portfolio-filter';
import { FlagKey, FLAGS } from '@unistocks/features/portfolios/flags';
import { mapSharedPortfolioToOption } from '@unistocks/features/shared-portfolios/shared-portfolio-utils';
import { t } from '@unistocks/localization';
import {
  AssetType,
  DateConstant,
  ExternalAssetSearchDto,
  SharedPortfolioDto
} from '@unistocks/proxy/axios-client';
import { useSharedPortfoliosGetListQuery } from '@unistocks/proxy/axios-client/SharedPortfoliosQuery';
import { assetValueFormatter } from '@unistocks/shared/utils/asset-utils';
import { dateConstantOptions } from '@unistocks/shared/utils/enums/date-constant';
import { extractOptionId, extractSymbol } from '@unistocks/shared/utils/list-utils';
import { Option } from '@unistocks/types';
import React, { useCallback } from 'react';
import { Separator } from 'tamagui';
import { z } from 'zod';

export const portfolioHistorySchemaFactory = () =>
  z.object({
    title: z.string().nullish(),
    portfolios: z
      .array(z.string())
      .nullish()
      .refine(
        value => value?.length === 1,
        t('::ValidationError:Required:Selection', { 0: t('::Portfolio') })
      ),
    period: requiredSchema(
      oneOf(dateConstantOptions.map(({ value }) => value)),
      t('::Dashboard:Settings:DateRange')
    ),
    portfolioComparisonSettings: z
      .object({
        index: z
          .looseObject({
            identifier: z.string(),
            dataSource: z.number()
          })
          .nullable(),
        sharedPortfolioId: z.string().nullable()
      })
      .nullable()
  });

const renderItemIcon = (item: Option<ExternalAssetSearchDto>) =>
  (item.value?.imageUri ?? item.value?.market) ? (
    <Image
      rounded
      size={'$1.5'}
      source={
        item.value?.imageUri ??
        (item.value?.market ? FLAGS[item.value.market as FlagKey] : undefined)
      }
    />
  ) : (
    <UnistocksCircle size={'$1.5'} />
  );

export const PortfolioHistorySettings: React.FC = () => {
  const { search } = useAssetSearch(AssetType.Index);
  const { data: sharedPortfolios } = useSharedPortfoliosGetListQuery(0, 100);

  const sharedPortfolioOptions = (sharedPortfolios?.items ?? []).map(mapSharedPortfolioToOption);

  const sharedPortfolioTransform: FormFieldTransform<string | null, SharedPortfolioDto | null> = {
    input: id => (id ? (sharedPortfolioOptions.find(o => o.value.id === id)?.value ?? null) : null),
    output: dto => dto?.id ?? null
  };

  const renderSharedPortfolioGroupHeader = useCallback(
    (group: string) =>
      sharedPortfolioOptions.find(o => o.value.creatorId === group)?.subTitle ?? '',
    [sharedPortfolioOptions]
  );

  return (
    <>
      <Form.Input
        name={'title'}
        label={t('::DisplayName')}
        helpText={t('::Dashboard:Settings:Title:HelpText')}
      />
      <PortfolioFilter
        portfolioSelectOverrides={{ multiple: false }}
        sectionSelectOverrides={{ hidden: true }}
        assetTypeSelectOverrides={{ hidden: true }}
      />
      <PeriodSelect
        isValidOption={dateConstant =>
          ![DateConstant.FiveYears, DateConstant.Max].includes(dateConstant)
        }
      />

      <Separator my={'$3'} />

      <Form.FieldContainer name="portfolioComparisonSettings">
        <Form.Typeahead<ExternalAssetSearchDto>
          name={'portfolioComparisonSettings.index'}
          label={t('::Chart:CompareIndex')}
          placeholder={t('::Chart:CompareIndex:Placeholder')}
          keyExtractor={extractSymbol}
          valueFormatter={assetValueFormatter}
          search={search}
          renderItemIcon={renderItemIcon}
        />
        <Form.Select<SharedPortfolioDto>
          name={'portfolioComparisonSettings.sharedPortfolioId'}
          label={t('::Chart:CompareSharedPortfolio')}
          placeholder={t('::Chart:CompareSharedPortfolio:Placeholder')}
          options={sharedPortfolioOptions}
          groupBy="creatorId"
          renderGroupHeader={renderSharedPortfolioGroupHeader}
          keyExtractor={extractOptionId}
          valueTransform={sharedPortfolioTransform}
        />
      </Form.FieldContainer>
    </>
  );
};

PortfolioHistorySettings.displayName = 'PortfolioHistorySettings';
